import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Plus,
  Trash2,
  CalendarDays,
  ListChecks,
  Rss,
  Copy,
  RotateCcw,
  CircleX,
} from 'lucide-react';

import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { SaveBar } from '@/components/ui/save-bar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useTemplates,
  useSaveTemplates,
  useBookings,
  useUpdateBookingState,
  useIcalTokens,
  useRotateIcalToken,
  useRevokeIcalToken,
  type AvailabilityTemplate,
  type BookingRow,
  type IcalToken,
} from '@/lib/booking';
import { useMe } from '@/lib/brief';
import { env } from '@/lib/env';
import { useBrief } from '@/lib/brief';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/calendar')({
  staticData: { crumb: 'Kalendarz wizyt' },
  component: CalendarPage,
});

const DAYS = [
  { v: 0, label: 'Pon' },
  { v: 1, label: 'Wt' },
  { v: 2, label: 'Śr' },
  { v: 3, label: 'Czw' },
  { v: 4, label: 'Pt' },
  { v: 5, label: 'Sob' },
  { v: 6, label: 'Niedz' },
] as const;

function CalendarPage() {
  const { data: brief } = useBrief();
  const templates = useTemplates();
  const save = useSaveTemplates();
  const bookings = useBookings();
  const updateState = useUpdateBookingState();

  const team = ((brief?.team ?? []) as Array<{ id: string; full_name: string }>) || [];

  const [items, setItems] = React.useState<AvailabilityTemplate[]>([]);
  const [dirty, setDirty] = React.useState(false);
  const [savedStatus, setSavedStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');

  React.useEffect(() => {
    if (templates.data && !dirty) setItems(templates.data);
  }, [templates.data, dirty]);

  const onSave = async () => {
    setSavedStatus('idle');
    try {
      await save.mutateAsync(items);
      setDirty(false);
      setSavedStatus('saved');
    } catch {
      setSavedStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Kalendarz wizyt
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Tygodniowy harmonogram pracy zespołu. Sloty generujemy w locie z
          szablonów — pacjent w trakcie rezerwacji widzi wolne terminy do 14 dni
          naprzód.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="size-5" aria-hidden="true" /> Harmonogram tygodniowy
          </CardTitle>
          <CardDescription>
            Wybierz lekarza, dzień tygodnia, godziny pracy i długość slotu. Możesz dodać kilka
            bloków na ten sam dzień (np. pn 09–13 + 15–19).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {team.length === 0 ? (
            <p className="rounded-md bg-warning/10 p-3 text-sm text-warning ring-1 ring-warning/20">
              Aby uruchomić harmonogram, najpierw dodaj członków zespołu w sekcji „Treść strony
              → Zespół".
            </p>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                onSave();
              }}
              className="space-y-4"
            >
              <ul role="list" className="space-y-3">
                {items.map((t, idx) => (
                  <li
                    key={idx}
                    className="grid items-end gap-3 rounded-[var(--radius-card)] bg-bg p-4 ring-1 ring-black/5 md:grid-cols-[1.4fr_repeat(4,minmax(0,1fr))_3rem]"
                  >
                    <Field label="Lekarz">
                      <Select
                        value={t.doctor_id}
                        onChange={(e) => {
                          const next = [...items];
                          next[idx] = { ...t, doctor_id: e.target.value };
                          setItems(next);
                          setDirty(true);
                        }}
                      >
                        {team.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.full_name}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Dzień">
                      <Select
                        value={String(t.weekday)}
                        onChange={(e) => {
                          const next = [...items];
                          next[idx] = { ...t, weekday: parseInt(e.target.value, 10) };
                          setItems(next);
                          setDirty(true);
                        }}
                      >
                        {DAYS.map((d) => (
                          <option key={d.v} value={d.v}>
                            {d.label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Od">
                      <Input
                        type="time"
                        value={t.start_time}
                        onChange={(e) => {
                          const next = [...items];
                          next[idx] = { ...t, start_time: e.target.value };
                          setItems(next);
                          setDirty(true);
                        }}
                      />
                    </Field>
                    <Field label="Do">
                      <Input
                        type="time"
                        value={t.end_time}
                        onChange={(e) => {
                          const next = [...items];
                          next[idx] = { ...t, end_time: e.target.value };
                          setItems(next);
                          setDirty(true);
                        }}
                      />
                    </Field>
                    <Field label="Slot (min)">
                      <Input
                        type="number"
                        min={5}
                        max={180}
                        value={t.slot_minutes}
                        onChange={(e) => {
                          const next = [...items];
                          next[idx] = {
                            ...t,
                            slot_minutes: Math.max(5, Math.min(180, Number(e.target.value))),
                          };
                          setItems(next);
                          setDirty(true);
                        }}
                      />
                    </Field>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Usuń"
                      onClick={() => {
                        setItems(items.filter((_, i) => i !== idx));
                        setDirty(true);
                      }}
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ul>

              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setItems([
                    ...items,
                    {
                      doctor_id: team[0]!.id,
                      weekday: 0,
                      start_time: '09:00',
                      end_time: '17:00',
                      slot_minutes: 30,
                      service_slugs: null,
                      buffer_minutes: 0,
                    },
                  ]);
                  setDirty(true);
                }}
              >
                <Plus className="size-4" aria-hidden="true" /> Dodaj blok
              </Button>

              <SaveBar
                saving={save.isPending}
                dirty={dirty}
                status={savedStatus}
                onCancel={() => {
                  setItems(templates.data ?? []);
                  setDirty(false);
                }}
              />
            </form>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListChecks className="size-5" aria-hidden="true" /> Wizyty
          </CardTitle>
          <CardDescription>
            Ostatnie 7 dni i terminy nadchodzące. Oznacz wizytę jako odbytą, no-show
            lub odwołaj.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {bookings.isPending ? (
            <p className="p-6 text-sm text-text-muted">Ładowanie…</p>
          ) : (bookings.data ?? []).length === 0 ? (
            <p className="p-6 text-sm text-text-muted">Brak wizyt. Po opublikowaniu strony i ustawieniu harmonogramu pacjenci zaczną rezerwować.</p>
          ) : (
            <BookingsTable
              bookings={bookings.data!}
              team={team}
              onState={(id, state, reason) => updateState.mutate({ id, state, reason })}
              busy={updateState.isPending}
            />
          )}
        </CardContent>
      </Card>

      <IcalSubscriptions team={team} />
    </div>
  );
}

function IcalSubscriptions({ team }: { team: Array<{ id: string; full_name: string }> }) {
  const me = useMe();
  const tokens = useIcalTokens();
  const rotate = useRotateIcalToken();
  const revoke = useRevokeIcalToken();
  const [copied, setCopied] = React.useState<string | null>(null);

  const slug = me.data?.tenant.slug;
  const apiBase = env.apiBaseUrl.replace(/\/$/, '');
  const urlFor = (t: IcalToken) =>
    slug
      ? `${apiBase}/api/booking/${slug}/ical/${encodeURIComponent(t.doctor_id)}?token=${t.token}`
      : '';

  const byDoctor = new Map<string, IcalToken>();
  for (const t of tokens.data ?? []) byDoctor.set(t.doctor_id, t);

  const onCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
    } catch {
      /* noop */
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Rss className="size-5" aria-hidden="true" /> Subskrypcje kalendarza (iCal)
        </CardTitle>
        <CardDescription>
          Każdy lekarz dostaje unikalny adres URL, który można dodać jako subskrybowany
          kalendarz w Google Calendar, Apple Calendar lub Outlooku — synchronizacja
          jednostronna (tylko-do-odczytu), odświeżana ~co godzinę. Rotacja unieważnia
          poprzedni link.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {team.length === 0 ? (
          <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
            Najpierw dodaj zespół w Treści strony → Zespół.
          </p>
        ) : (
          <ul role="list" className="space-y-2">
            {team.map((doctor) => {
              const tok = byDoctor.get(doctor.id);
              return (
                <li
                  key={doctor.id}
                  className="rounded-md bg-surface p-4 ring-1 ring-black/5"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-display text-base font-semibold text-text">
                      {doctor.full_name || doctor.id}
                    </p>
                    <p className="text-xs text-text-subtle">
                      {tok
                        ? tok.last_used_at
                          ? `Ostatnie pobranie: ${new Date(tok.last_used_at * 1000).toLocaleString('pl-PL')}`
                          : 'Jeszcze nie pobrane'
                        : 'Brak aktywnej subskrypcji'}
                    </p>
                  </div>

                  {tok ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <input
                        readOnly
                        value={urlFor(tok)}
                        onFocus={(e) => e.currentTarget.select()}
                        className="h-9 flex-1 min-w-0 truncate rounded-md bg-bg px-3 font-mono text-xs ring-1 ring-border"
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => onCopy(urlFor(tok), doctor.id)}
                      >
                        <Copy className="size-3.5" aria-hidden="true" />
                        {copied === doctor.id ? 'Skopiowano' : 'Kopiuj'}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => rotate.mutate(doctor.id)}
                        disabled={rotate.isPending}
                      >
                        <RotateCcw className="size-3.5" aria-hidden="true" />
                        Rotuj
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => revoke.mutate(doctor.id)}
                        disabled={revoke.isPending}
                      >
                        <CircleX className="size-3.5" aria-hidden="true" />
                        Usuń
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => rotate.mutate(doctor.id)}
                        disabled={rotate.isPending}
                      >
                        <Rss className="size-3.5" aria-hidden="true" />
                        Wygeneruj link subskrypcji
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function BookingsTable({
  bookings,
  team,
  onState,
  busy,
}: {
  bookings: BookingRow[];
  team: Array<{ id: string; full_name: string }>;
  onState: (id: string, state: 'cancelled' | 'no_show' | 'completed', reason?: string) => void;
  busy: boolean;
}) {
  const doctorName = (id: string) => team.find((t) => t.id === id)?.full_name ?? id;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[56rem] text-sm">
        <thead className="bg-surface text-left text-xs uppercase tracking-wide text-text-subtle">
          <tr>
            <th className="px-4 py-3 font-medium">Termin</th>
            <th className="px-4 py-3 font-medium">Pacjent</th>
            <th className="px-4 py-3 font-medium">Lekarz</th>
            <th className="px-4 py-3 font-medium">Usługa</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium text-right">Akcje</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border bg-bg">
          {bookings.map((b) => {
            const date = new Date(b.start_at * 1000);
            const dateStr = date.toLocaleString('pl-PL', {
              timeZone: 'Europe/Warsaw',
              dateStyle: 'medium',
              timeStyle: 'short',
            });
            return (
              <tr key={b.id}>
                <td className="px-4 py-3 text-text">{dateStr}</td>
                <td className="px-4 py-3">
                  <p className="font-medium text-text">{b.patient_name}</p>
                  <p className="text-xs text-text-subtle font-mono">{b.patient_phone}</p>
                </td>
                <td className="px-4 py-3 text-text-muted">{doctorName(b.doctor_id)}</td>
                <td className="px-4 py-3 text-text-muted">{b.service_slug ?? '—'}</td>
                <td className="px-4 py-3">
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-xs ring-1',
                      b.state === 'confirmed'
                        ? 'bg-success/10 text-success ring-success/20'
                        : b.state === 'no_show'
                        ? 'bg-error/10 text-error ring-error/20'
                        : b.state === 'cancelled'
                        ? 'bg-surface text-text-muted ring-border'
                        : 'bg-accent/10 text-accent ring-accent/20'
                    )}
                  >
                    {b.state}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {b.state === 'confirmed' ? (
                    <div className="inline-flex items-center gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => onState(b.id, 'completed')}
                      >
                        Odbyta
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => onState(b.id, 'no_show')}
                      >
                        No-show
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => onState(b.id, 'cancelled', 'Anulowano przez recepcję')}
                      >
                        Odwołaj
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-text-subtle">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
